import {
  Blocks,
  Bolt,
  BookMarked,
  BookOpen,
  Box,
  Bug,
  Building2,
  ChartNoAxesColumn,
  FolderCode,
  House,
  Settings,
  SquareUser,
  UserCog,
  Users,
  BadgeDollarSign,
  Activity,
} from 'lucide-react';
import { ROUTE_PATHS } from '../../routes/consts';

export const SIDEBAR_ICON_COLOR_PRIMARY = 'text-text-interactive-emphasis-idle';
export const SIDEBAR_ICON_COLOR_SECONDARY = 'text-text-general-neutral-mid';
export const USER_MENU_ICON_SIZE = 16;

export const ITEM_ID = {
  TEAM: 'team',
  TEAMS_MANAGEMENT: 'teams-management',
  HOME: 'home',
  ORGANIZATION: 'users-management',
  PLANS: 'plans',
  PRODUCTS: 'products',
  PLATFORM: 'platform',
  CAPABILITIES: 'capabilities',
  QODO_MERGE_ANALYTICS: 'qodo-merge-analytics',
  QODO_GEN_ANALYTICS: 'qodo-gen-analytics',
  QODO_COVER: 'qodo-cover',
  USERS: 'users',
  ANALYTICS: 'analytics',
  INSTALLATIONS: 'installation',
  INTEGRATIONS: 'integrations',
  CONFIGURATION: 'configuration',
  MONITOR: 'monitor',
  EXTENSIONS: 'extensions',
  EXTRA_CONTEXT: 'extra-context',
  MORE: 'more',
  RULES: 'rules',
  FINDINGS: 'findings',
  WIKI: 'wiki',
  TEAM_PROFILES: 'team-profiles',
  ACCOUNT: 'account',
  SETTINGS: 'settings',
  TICKETS: 'tickets',
  MANAGEMENT_REPOSITORIES: 'repositories',
  // TODO: Remove QODO_MERGE, QODO_GEN, QODO_INDEX ITEM_IDs once deprecated
  // https://linear.app/qodo/issue/PLT-2593/fe-remove-qodo-merge-qodo-gen-qodo-index-routes-once-deprecated
  QODO_MERGE: 'qodo-merge',
  QODO_INDEX: 'qodo-index',
  QODO_GEN: 'qodo-gen',
  BILLING: 'billing',
  SANDBOX_CONFIG: 'sandbox-config',
} as const;

export const DISABLED_REASON_KEYS = {
  ORGANIZATION_OWNER_ONLY: 'organization-owner-only',
  MAX_TIER_LEVEL: 'max-tier-level',
} as const;

export const SIDEBAR_UPGRADE_MODE = {
  SELF_SERVE: 'self-serve',
  LEGACY: 'legacy',
  HIDDEN: 'hidden',
} as const;

export type SidebarUpgradeMode =
  (typeof SIDEBAR_UPGRADE_MODE)[keyof typeof SIDEBAR_UPGRADE_MODE];

export const ICONS_MAP: Partial<
  Record<(typeof ITEM_ID)[keyof typeof ITEM_ID], React.ReactNode>
> = {
  [ITEM_ID.HOME]: <House className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.MONITOR]: <Activity className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.INTEGRATIONS]: <Blocks className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.RULES]: <BookMarked className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.FINDINGS]: <Bug className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.WIKI]: <BookOpen className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.TEAM_PROFILES]: <UserCog className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.ANALYTICS]: (
    <ChartNoAxesColumn className={SIDEBAR_ICON_COLOR_SECONDARY} />
  ),
  [ITEM_ID.CONFIGURATION]: <Bolt className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.ACCOUNT]: <Building2 className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.ORGANIZATION]: (
    <SquareUser className={SIDEBAR_ICON_COLOR_SECONDARY} />
  ),
  [ITEM_ID.TEAM]: <Users className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.BILLING]: (
    <BadgeDollarSign className={SIDEBAR_ICON_COLOR_SECONDARY} />
  ),
  [ITEM_ID.TEAMS_MANAGEMENT]: (
    <Users className={SIDEBAR_ICON_COLOR_SECONDARY} />
  ),
  [ITEM_ID.SETTINGS]: <Settings className={SIDEBAR_ICON_COLOR_SECONDARY} />,
  [ITEM_ID.MANAGEMENT_REPOSITORIES]: (
    <FolderCode className={SIDEBAR_ICON_COLOR_SECONDARY} />
  ),
  [ITEM_ID.SANDBOX_CONFIG]: <Box className={SIDEBAR_ICON_COLOR_SECONDARY} />,
};

export const ACCOUNT_IDS = [
  ROUTE_PATHS.BILLING_TRANSACTION_HISTORY,
  ROUTE_PATHS.BILLING_ANALYTICS,
  ROUTE_PATHS.BILLING,
  ROUTE_PATHS.ACCOUNT,
  ROUTE_PATHS.TEAMS_MANAGEMENT,
  ROUTE_PATHS.TEAM,
  ROUTE_PATHS.ORGANIZATION,
  ROUTE_PATHS.SETTINGS,
] as const;

export type AccountId = (typeof ACCOUNT_IDS)[number];

export const REPOSITORIES_IDS = [
  ROUTE_PATHS.MANAGEMENT_REPOSITORIES,
  ROUTE_PATHS.REPOSITORIES,
  ROUTE_PATHS.INSTALLATIONS,
] as const;

export type RepositoryId = (typeof REPOSITORIES_IDS)[number];
